import React, { useState, useEffect } from 'react';
import { Clock, ChevronDown, Globe } from 'lucide-react';

const CronBuilder = ({ value, onChange }) => {
  const [showPresets, setShowPresets] = useState(false);
  const [cronExpression, setCronExpression] = useState(value || '0 8 * * *');
  const [userTimezone, setUserTimezone] = useState('');
  const [serverTimezone, setServerTimezone] = useState('UTC');
  
  useEffect(() => {
    // Get user's timezone
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimezone(tz);
  }, []);

  const presets = [
    { name: 'Every minute', cron: '* * * * *', description: 'Runs every minute' },
    { name: 'Every 5 minutes', cron: '*/5 * * * *', description: 'Runs every 5 minutes' },
    { name: 'Every 15 minutes', cron: '*/15 * * * *', description: 'Runs every 15 minutes' },
    { name: 'Every 30 minutes', cron: '*/30 * * * *', description: 'Runs every 30 minutes' },
    { name: 'Every hour', cron: '0 * * * *', description: 'Runs at the start of every hour' },
    { name: 'Every day at 8:00 AM', cron: '0 8 * * *', description: 'Runs every day at 8:00 AM' },
    { name: 'Every day at 9:00 AM', cron: '0 9 * * *', description: 'Runs every day at 9:00 AM' },
    { name: 'Every day at midnight', cron: '0 0 * * *', description: 'Runs every day at midnight' },
    { name: 'Every Monday at 9:00 AM', cron: '0 9 * * 1', description: 'Runs every Monday at 9:00 AM' },
    { name: 'Every weekday at 9:00 AM', cron: '0 9 * * 1-5', description: 'Monday through Friday at 9:00 AM' },
    { name: 'First day of every month', cron: '0 0 1 * *', description: 'Midnight on the first day of each month' },
  ];

  useEffect(() => {
    if (value !== cronExpression) {
      setCronExpression(value || '0 8 * * *');
    }
  }, [value]);

  const handleChange = (newValue) => {
    setCronExpression(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const selectPreset = (preset) => {
    handleChange(preset.cron);
    setShowPresets(false);
  };

  const getDescription = () => {
    const preset = presets.find(p => p.cron === cronExpression);
    return preset ? preset.description : 'Custom schedule';
  };

  // Parse cron expression to get time
  const getTimeFromCron = (cron) => {
    try {
      const parts = cron.split(' ');
      if (parts.length >= 2) {
        const minute = parts[0];
        const hour = parts[1];
        if (!minute.includes('*') && !hour.includes('*') && !minute.includes('/') && !hour.includes('/')) {
          return { hour: parseInt(hour), minute: parseInt(minute) };
        }
      }
    } catch (e) {
      // ignore parse errors
    }
    return null;
  };

  // Convert server time to local time display
  const getLocalTimeDisplay = () => {
    const time = getTimeFromCron(cronExpression);
    if (!time) return null;

    // Assume server is in UTC+7 (Thailand time) - adjust this based on your server
    // For accurate timezone, you'd need to get this from the server
    const serverOffset = 7; // UTC+7 (Bangkok time)
    const localOffset = new Date().getTimezoneOffset() / -60;
    const timeDiff = localOffset - serverOffset;

    let localHour = time.hour + timeDiff;
    if (localHour < 0) localHour += 24;
    if (localHour >= 24) localHour -= 24;

    return {
      serverTime: `${time.hour.toString().padStart(2, '0')}:${time.minute.toString().padStart(2, '0')}`,
      localTime: `${localHour.toString().padStart(2, '0')}:${time.minute.toString().padStart(2, '0')}`,
      isDifferent: timeDiff !== 0
    };
  };

  const localTimeInfo = getLocalTimeDisplay();

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        <Clock className="inline h-4 w-4 mr-1" />
        Schedule (Cron Expression)
      </label>
      
      <div className="flex space-x-2">
        <input
          type="text"
          value={cronExpression}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="5 18 * * *"
          className="flex-1 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm font-mono"
        />
        <button
          type="button"
          onClick={() => setShowPresets(!showPresets)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          Presets
          <ChevronDown className="ml-2 h-4 w-4" />
        </button>
      </div>

      <div className="space-y-1">
        <p className="text-sm text-gray-500">{getDescription()}</p>
        
        {/* Timezone Info */}
        {localTimeInfo && localTimeInfo.isDifferent && (
          <div className="flex items-start space-x-2 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 p-2 rounded-md">
            <Globe className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Timezone Information:</div>
              <div className="mt-1 space-y-0.5">
                <div>🖥️ Server Time (UTC+7): <span className="font-mono font-bold">{localTimeInfo.serverTime}</span></div>
                <div>🌍 Your Local Time: <span className="font-mono font-bold">{localTimeInfo.localTime}</span> ({userTimezone})</div>
              </div>
              <div className="mt-1 text-xs opacity-75">
                The schedule runs in server time (UTC+7 Bangkok). Your schedule will execute at {localTimeInfo.localTime} your local time.
              </div>
            </div>
          </div>
        )}
        
        {localTimeInfo && !localTimeInfo.isDifferent && (
          <div className="flex items-center space-x-2 text-xs text-green-600 dark:text-green-400">
            <Globe className="h-3 w-3" />
            <span>Your timezone matches server timezone (UTC+7)</span>
          </div>
        )}
      </div>

      {/* Presets Dropdown */}
      {showPresets && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black dark:ring-gray-600 ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {presets.map((preset, index) => (
            <button
              key={index}
              type="button"
              onClick={() => selectPreset(preset)}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700"
            >
              <div className="font-medium text-gray-900 dark:text-gray-100">{preset.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{preset.cron}</div>
              <div className="text-xs text-gray-400 dark:text-gray-500">{preset.description}</div>
            </button>
          ))}
        </div>
      )}

      {/* Cron Format Help */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 text-xs text-gray-600 dark:text-gray-400">
        <div className="font-medium mb-1 text-gray-900 dark:text-gray-100">Cron Format: minute hour day month weekday</div>
        <div className="space-y-1">
          <div>* = any value</div>
          <div>*/n = every n units</div>
          <div>1-5 = range from 1 to 5</div>
          <div>1,3,5 = specific values</div>
        </div>
        <div className="mt-2">
          <strong>Examples:</strong>
          <div className="mt-1 space-y-1">
            <div><code className="bg-white dark:bg-gray-700 px-1 rounded">5 18 * * *</code> = Every day at 18:05 (6:05 PM) server time</div>
            <div><code className="bg-white dark:bg-gray-700 px-1 rounded">0 8 * * *</code> = Every day at 8:00 AM server time</div>
            <div><code className="bg-white dark:bg-gray-700 px-1 rounded">30 14 * * 1-5</code> = Weekdays at 14:30 (2:30 PM) server time</div>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="font-medium text-amber-600 dark:text-amber-400">⏰ Important:</div>
          <div className="mt-1">All schedules run in <strong>server timezone (UTC+7 Bangkok)</strong>. Use the timezone info above to convert to your local time.</div>
        </div>
      </div>
    </div>
  );
};

export default CronBuilder;

